import React, { useState, useEffect } from 'react';
import ScrollX from '../../components/ScrollX';
import { supabase } from '../../supabase';
import { workDone, actorKey, median, fmtDuration } from '../../utils/activityLog';
import { DOC_KEYS } from '../../utils/docVocab';

// ═══ PRODUCTIVITY → WORK DONE — per person, over the card's date range (Philip 2026-10-23) ═══
// Counts come from the activity log's CHANGES (the database's own record — utils/activityLog workDone), so work can't
// go uncounted because a screen forgot to log it. How fast (medians, with how many cases):
//   Intake      a document uploaded → filed (file_exchange.created_at → the change that set filed_at), to who filed it
//   Approval    a bill submitted → approved (tax_bills.submitted_at → approved_at), to who approved it
//   Transmit    a bill approved → its transmittal sent (→ tax_bill_transmittals.transmitted_at), to who sent it
//   Notice      a notice filed → its parcel-year's Proposed values first entered, to who entered them
// The log starts on 2026-10-23, so ranges before then have no counts. Reading it needs "Can view Activity".
const localMid = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };

async function pageAll(build) {
  const out = [];
  for (let from = 0; ; from += 1000) {
    const { data, error } = await build().range(from, from + 999);
    if (error) throw error;
    out.push(...(data || []));
    if (!data || data.length < 1000) return out;
  }
}
async function byIds(table, cols, col, ids, extra = (q) => q) {
  const out = [];
  const list = [...new Set(ids.filter(Boolean))];
  for (let i = 0; i < list.length; i += 100) {
    const { data, error } = await extra(supabase.from(table).select(cols).in(col, list.slice(i, i + 100)));
    if (error) throw error;
    out.push(...(data || []));
  }
  return out;
}

export default function WorkDone({ start, end, staffUser, c }) {
  const canView = !!staffUser?.can_view_activity;
  const [state, setState] = useState({ loading: true, err: '', rows: [], other: 0 });

  useEffect(() => {
    if (!canView || !start || !end) return undefined;
    let cancelled = false;
    (async () => {
      setState((s) => ({ ...s, loading: true, err: '' }));
      try {
        const from = localMid(start).toISOString();
        const endEx = localMid(end); endEx.setDate(endEx.getDate() + 1);
        const to = endEx.toISOString();
        const [{ data: staff }, log, approved, sent] = await Promise.all([
          supabase.from('ntrg_staff').select('id, name, active').order('name'),
          pageAll(() => supabase.from('activity_log').select('staff_id, staff_user, source, table_name, op, row_id, parcel_id, tax_year, changes, created_at')
            .not('table_name', 'is', null).gte('created_at', from).lt('created_at', to).order('created_at')),
          pageAll(() => supabase.from('tax_bills').select('approved_by, submitted_at, approved_at').gte('approved_at', from).lt('approved_at', to).order('approved_at')),
          pageAll(() => supabase.from('tax_bill_transmittals').select('id, transmitted_by, transmitted_at').gte('transmitted_at', from).lt('transmitted_at', to).order('transmitted_at')),
        ]);
        const counts = workDone(log);
        const durs = new Map();   // actorKey → { intake: [], approval: [], transmit: [], notice: [] }
        const push = (k, kind, ms) => { if (!k || !Number.isFinite(ms) || ms < 0) return; const d = durs.get(k) || { intake: [], approval: [], transmit: [], notice: [] }; d[kind].push(ms); durs.set(k, d); };

        // Intake: uploaded → filed.
        const filed = log.filter((r) => r.table_name === 'file_exchange' && r.op === 'edit' && Array.isArray(r.changes?.filed_at) && !r.changes.filed_at[0] && r.changes.filed_at[1]);
        const fx = await byIds('file_exchange', 'id, created_at', 'id', filed.map((r) => r.row_id));
        const fxAt = new Map(fx.map((x) => [x.id, x.created_at]));
        filed.forEach((r) => { const up = fxAt.get(r.row_id); if (up) push(actorKey(r), 'intake', new Date(r.changes.filed_at[1]).getTime() - new Date(up).getTime()); });

        // Approval: submitted → approved, to the approver.
        approved.forEach((b) => { if (b.approved_by && b.submitted_at) push(b.approved_by, 'approval', new Date(b.approved_at) - new Date(b.submitted_at)); });

        // Transmit: approved → sent, to who sent it (the latest approval among the transmittal's bills).
        const tb = await byIds('tax_bills', 'transmittal_id, approved_at', 'transmittal_id', sent.map((t) => t.id));
        sent.forEach((t) => {
          const ap = tb.filter((b) => b.transmittal_id === t.id && b.approved_at).map((b) => new Date(b.approved_at).getTime());
          if (t.transmitted_by && ap.length) push(t.transmitted_by, 'transmit', new Date(t.transmitted_at).getTime() - Math.max(...ap));
        });

        // Notice → values: the first Proposed values added for a parcel-year, from its earliest filed notice.
        const firstVals = new Map();   // parcel|year → log row
        log.filter((r) => r.table_name === 'parcel_appeals' && r.op === 'add' && r.parcel_id && (r.changes?.appeal_level || 'proposed') === 'proposed')
          .forEach((r) => { const k = `${r.parcel_id}|${r.tax_year}`; if (!firstVals.has(k)) firstVals.set(k, r); });
        if (firstVals.size) {
          const notices = await byIds('file_exchange', 'parcel_id, year_subfolder, filed_at', 'parcel_id', [...firstVals.values()].map((r) => r.parcel_id),
            (q) => q.eq('folder_key', DOC_KEYS.notices).not('filed_at', 'is', null));
          const firstNotice = new Map();
          notices.forEach((n) => { const k = `${n.parcel_id}|${n.year_subfolder}`; const t = new Date(n.filed_at).getTime(); if (!firstNotice.has(k) || t < firstNotice.get(k)) firstNotice.set(k, t); });
          firstVals.forEach((r, k) => { if (firstNotice.has(k)) push(actorKey(r), 'notice', new Date(r.created_at).getTime() - firstNotice.get(k)); });
        }

        const people = (staff || []).filter((s) => s.active !== false || counts.has(s.id));
        const rows = people.map((s) => {
          const w = counts.get(s.id) || {};
          const d = durs.get(s.id) || { intake: [], approval: [], transmit: [], notice: [] };
          return { id: s.id, name: s.name, ...w, intake: d.intake, approval: d.approval, transmit: d.transmit, notice: d.notice };
        });
        const known = new Set(people.map((s) => s.id));
        const other = [...counts.entries()].filter(([k]) => !known.has(k)).reduce((n, [, w]) => n + w.changes, 0);
        if (!cancelled) setState({ loading: false, err: '', rows, other });
      } catch (e) {
        if (!cancelled) setState({ loading: false, err: e.message || String(e), rows: [], other: 0 });
      }
    })();
    return () => { cancelled = true; };
  }, [start, end, canView]);

  const th = { padding: '9px 10px', textAlign: 'right', fontSize: '11px', fontWeight: '700', color: c.muted, background: c.th, borderBottom: `1px solid ${c.border}`, whiteSpace: 'nowrap' };
  const thL = { ...th, textAlign: 'left' };
  const td = { padding: '9px 10px', textAlign: 'right', fontSize: '12px', color: c.text, borderBottom: `1px solid ${c.border}`, fontFamily: "'DM Mono',monospace" };
  const tdL = { ...td, textAlign: 'left', fontFamily: "'Public Sans',system-ui,sans-serif", fontWeight: 600 };
  const n = (v) => (v ? v.toLocaleString('en-US') : <span style={{ color: c.muted }}>0</span>);
  const dur = (list) => (list && list.length ? <span title={`median of ${list.length}`}>{fmtDuration(median(list))}<span style={{ color: c.muted, fontSize: '10px' }}> ·{list.length}</span></span> : <span style={{ color: c.muted }}>—</span>);

  return (
    <div style={{ marginTop: '22px' }}>
      <div style={{ fontSize: '15px', fontWeight: 700, color: c.text, marginBottom: '4px' }}>Work done</div>
      <div style={{ fontSize: '12px', color: c.muted, marginBottom: '10px' }}>
        From the activity log — what each person changed in this range, and how fast (median; the small number is how many).
        The log starts on 10/23/2026.
      </div>
      {!canView ? (
        <div style={{ fontSize: '12px', color: c.muted }}>Needs <strong>Can view Activity</strong> (Table Management → Employees).</div>
      ) : state.err ? (
        <div style={{ fontSize: '12px', color: 'var(--red)' }}>Could not load the activity log: {state.err}</div>
      ) : (
        <ScrollX style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '10px', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1100px' }}>
            <thead><tr>
              <th style={thL}>Staffer</th>
              <th style={th} title="Parcel-years whose values or deductions this person added or changed">Values</th>
              <th style={th} title="Notices added">Notices</th>
              <th style={th} title="Decisions set or changed">Decisions</th>
              <th style={th} title="Tax bills added (typed or scanned)">Bills in</th>
              <th style={th} title="Tax bills this person approved">Approved</th>
              <th style={th} title="Documents filed">Filed</th>
              <th style={th} title="Notes added">Notes</th>
              <th style={th} title="Clients, properties and parcels added">Added</th>
              <th style={th} title="Every change in the log">All changes</th>
              <th style={th} title="Document uploaded → filed, for documents this person filed">Intake</th>
              <th style={th} title="Bill submitted → approved, for bills this person approved">Approval</th>
              <th style={th} title="Bill approved → transmittal sent, for transmittals this person sent">Transmit</th>
              <th style={th} title="Notice filed → Proposed values first entered, for parcel-years this person entered">Notice → values</th>
            </tr></thead>
            <tbody>
              {state.loading ? (
                <tr><td colSpan={14} style={{ ...td, textAlign: 'center', color: c.muted, padding: '22px' }}>Loading…</td></tr>
              ) : state.rows.map((r) => (
                <tr key={r.id}>
                  <td style={tdL}>{r.name}</td>
                  <td style={td}>{n(r.valuesYears)}</td><td style={td}>{n(r.notices)}</td><td style={td}>{n(r.decisions)}</td>
                  <td style={td}>{n(r.billsEntered)}</td><td style={td}>{n(r.billsApproved)}</td><td style={td}>{n(r.docsFiled)}</td>
                  <td style={td}>{n(r.notes)}</td><td style={td}>{n(r.recordsAdded)}</td><td style={td}>{n(r.changes)}</td>
                  <td style={td}>{dur(r.intake)}</td><td style={td}>{dur(r.approval)}</td><td style={td}>{dur(r.transmit)}</td><td style={td}>{dur(r.notice)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!state.loading && state.other > 0 && (
            <div style={{ padding: '8px 12px', fontSize: '11px', color: c.muted }}>Plus {state.other.toLocaleString('en-US')} changes by scripts, the SQL editor or the server with no person (imports, data fixes).</div>
          )}
        </ScrollX>
      )}
    </div>
  );
}
